import React, {useEffect, useState, ChangeEvent, FormEvent} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import useGetCategories from '../hooks/useGetCategories';
import useGetTags from '../hooks/useGetTags';
import {useAuth} from "../hooks/useAuth";
import styles from './EditPostPage.module.css';
import useGetPost from "../hooks/useGetPost";
import useUpdatePost from "../hooks/useUpdatePost";

interface FormImage {
    id?: number;
    file: File | null;
    preview: string;
    caption: string;
}

interface Category {
    id: number;
    name: string;
    description: string;
}

interface Tag {
    id: number;
    name: string;
}

interface FormDataState {
    title: string;
    content: string;
    excerpt: string;
    category: string;
    tags: number[];
    images: FormImage[];
}

const EditPostPage = () => {
    const {postId} = useParams<{ postId: string }>();
    const postIdNumber = postId ? parseInt(postId) : -1;
    const navigate = useNavigate();
    const {user} = useAuth();
    const {
        getCategories,
        isLoading: categoriesLoading,
        error: categoriesError,
        data: categoriesData
    } = useGetCategories();
    const {getTags, isLoading: tagsLoading, error: tagsError, data: tagsData} = useGetTags();
    const {getPost, isLoading: postLoading, error: postError, data: postData} = useGetPost();

    const [formData, setFormData] = useState<FormDataState>({
        title: '',
        content: '',
        excerpt: '',
        category: '',
        tags: [],
        images: []
    });

    const [currentTag, setCurrentTag] = useState<string>('');
    const [currentImage, setCurrentImage] = useState<{ file: File | null; caption: string }>({
        file: null,
        caption: ''
    });

    const [isUpdating, setIsUpdating] = useState(false);
    const [updateMessage, setUpdateMessage] = useState('');
    const {updatePost, isLoading, error, data} = useUpdatePost()
    // Загрузка данных при монтировании компонента
    useEffect(() => {
        getCategories();
        getTags();

        if (postIdNumber > 0) {
            getPost(postIdNumber);
        }
    }, [postIdNumber]);

    // Обновление formData когда загружаются данные поста
    useEffect(() => {
        if (postData) {
            setFormData({
                title: postData.title || '',
                content: postData.content || '',
                excerpt: postData.excerpt || '',
                category: postData.id ? postData.id.toString() : '',
                tags: postData.tags ? postData.tags.map((tag: any) => tag.id) : [],
                images: postData.images ? postData.images.map((img: any) => ({
                    id: img.id,
                    file: null,
                    preview: img.url || 'https://via.placeholder.com/150',
                    caption: img.caption || ''
                })) : []
            });
        }
    }, [postData]);


    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddTag = () => {
        if (currentTag.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, parseInt(currentTag)]
            }));
            setCurrentTag('');
        }
    };

    const handleRemoveTag = (index: number) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index)
        }));
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (file) {
            setCurrentImage(prev => ({...prev, file}));
        }
    };

    const handleAddImage = () => {
        if (currentImage.file) {
            const imageUrl = URL.createObjectURL(currentImage.file);
            setFormData(prev => ({
                ...prev,
                images: [
                    ...prev.images,
                    {
                        file: currentImage.file,
                        preview: imageUrl,
                        caption: currentImage.caption
                    }
                ]
            }));
            setCurrentImage({file: null, caption: ''});
        }
    };

    const handleRemoveImage = (index: number) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        try {
            const postDataToSend = {
                title: formData.title,
                content: formData.content,
                excerpt: formData.excerpt,
                category: formData.category,
                tags: formData.tags,
                images: formData.images.map(img => ({
                    id: img.id,
                    caption: img.caption,
                }))
            };
            // @ts-ignore
            const result = await updatePost(postIdNumber, postDataToSend);
            navigate(`/post/${postId}`);

        } catch (err) {
            console.error('Ошибка при обновлении поста:', err);
            setUpdateMessage('Ошибка! Сервер предпочел кофе работе... ☕️');
        }
    };

    if (!user) {
        return (
            <div className={styles.editPostPage}>
                <h1>Редактирование поста</h1>
                <div className={styles.error}>
                    ❌ Для редактирования поста необходимо авторизоваться (и принести печеньку 🍪)
                </div>
            </div>
        );
    }

    if (postLoading) {
        return (
            <div className={styles.editPostPage}>
                <div className={styles.header}>
                    <h1>Редактирование поста</h1>
                    <p className={styles.subtitle}>Загружаем ваш шедевр... ⏳</p>
                </div>
                <div className={styles.loading}>
                    <p>Идет загрузка данных (надеемся, сервер не пьет кофе ☕)</p>
                </div>
            </div>
        );
    }

    if (postError) {
        return (
            <div className={styles.editPostPage}>
                <div className={styles.header}>
                    <h1>Редактирование поста</h1>
                    <p className={styles.subtitle}>Что-то пошло не так... 😵</p>
                </div>
                <div className={styles.error}>
                    <p>Не удалось загрузить данные поста: {postError}</p>
                    <button
                        onClick={() => postIdNumber > 0 && getPost(postIdNumber)}
                        className={styles.retryButton}
                    >
                        🔄 Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.editPostPage}>
            <div className={styles.header}>
                <h1>Редактирование поста</h1>
                <p className={styles.subtitle}>Где хорошие идеи становятся великими (иногда после 5-й попытки) 🚀</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.postForm}>
                <div className={styles.formGroup}>
                    <label htmlFor="title">
                        Заголовок <span className={styles.hint}>(Тот, что привлечет всеобщее внимание!)</span>
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        placeholder="Например: 'Как я победил баг в 3 часа ночи и обрел просветление'"
                    />
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="content">
                        Содержание <span className={styles.hint}>(Здесь можно жаловаться на баги)</span>
                    </label>
                    <textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        rows={8}
                        required
                        placeholder="Расскажите свою историю так, чтобы читатели плакали... от смеха или сочувствия"
                    />
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="excerpt">
                        Краткое описание <span className={styles.hint}>(Как в фильме - только самое интересное)</span>
                    </label>
                    <textarea
                        id="excerpt"
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Краткость - сестра таланта (и ленивого читателя)"
                    />
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="category">
                        Категория <span className={styles.hint}>(Куда же мы без категоризации?)</span>
                    </label>
                    <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Выберите категорию (это обязательно!)</option>
                        {categoriesData?.map((category: Category) => (
                            <option key={category.id} value={category.id.toString()}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>
                        Теги <span className={styles.hint}>(Выберите теги, иначе алгоритмы нас не простят! 🤖)</span>
                    </label>
                    <div className={styles.tagsInput}>
                        <select
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                        >
                            <option value="">Выберите тег (или придумайте новый)</option>
                            {tagsData?.map((tag: Tag) => (
                                <option key={tag.id} value={tag.id.toString()}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={handleAddTag}
                            className={styles.addButton}
                        >
                            ➕ Добавить тег
                        </button>
                    </div>

                    {formData.tags.length > 0 && (
                        <div className={styles.tagsList}>
                            {formData.tags.map((tagId, index) => {
                                const tag = tagsData?.find((t: Tag) => t.id === tagId);
                                return (
                                    <span key={index} className={styles.tag}>
                                        {tag ? tag.name : `#${tagId}`}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(index)}
                                            className={styles.removeTagButton}
                                        >
                                            ×
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label>
                        Изображения <span className={styles.hint}>(Картинка стоит тысячи слов... и пары гигабайт трафика 📸)</span>
                    </label>
                    <div className={styles.imageInput}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                        <input
                            type="text"
                            placeholder="Подпись к изображению (например: 'Мое кофе утром')"
                            value={currentImage.caption}
                            onChange={(e) => setCurrentImage(prev => ({...prev, caption: e.target.value}))}
                        />
                        <button
                            type="button"
                            onClick={handleAddImage}
                            disabled={!currentImage.file}
                            className={styles.addButton}
                        >
                            📤 Загрузить изображение
                        </button>
                    </div>

                    {formData.images.length > 0 && (
                        <div className={styles.imagesList}>
                            {formData.images.map((image, index) => (
                                <div key={index} className={styles.imageItem}>
                                    <img src={image.preview} alt={image.caption}/>
                                    <p>{image.caption}</p>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveImage(index)}
                                        className={styles.removeButton}
                                    >
                                        Удалить
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className={styles.cancelButton}
                    >
                        🏃‍♂️ Сбежать без сохранения
                    </button>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isUpdating}
                    >
                        {isUpdating ? '💾 Сохраняем...' : '✨ Опубликовать шедевр'}
                    </button>
                </div>

                {updateMessage && (
                    <div className={updateMessage.includes('Ошибка') ? styles.errorMessage : styles.successMessage}>
                        {updateMessage}
                    </div>
                )}
            </form>

            <div className={styles.devNotes}>
                <p>💡 Совет от профессионала: Ctrl+S сохраняет файл, но не пост. Мы уже проверяли!</p>
            </div>
        </div>
    );
};

export default EditPostPage;